/**
 * Markup de la galería `/briefings.html` — Fase 7g.1.
 *
 * Estructura simple: header con título + botón "Nuevo briefing", y
 * una tabla con una fila por briefing. Cada fila es clickable y
 * navega a la vista ficha `/briefing/ficha.html?id=<id>`. Desde la
 * ficha se salta al editor con el botón EDITAR (si hay permiso). El
 * botón X de la última columna (visible si el user puede borrar este
 * briefing) intercepta el click para no navegar y abre el modal de
 * confirmación.
 *
 * Sin paginación ni filtros aún (cap superior de unos pocos cientos
 * de briefings para 20 pilotos no lo necesita en MVP). Se añadirán
 * cuando el volumen lo justifique.
 */

import type {
  AuthenticatedPilot,
  Briefing,
  MissionSummary,
} from '@51esc/shared';
import { canDeleteBriefing } from '../auth/permissions.js';
import type { BriefingMission } from '../briefing/api.js';

/**
 * Shell HTML de la galería con la tabla en estado "cargando".
 *
 * `user === null` (anónimo) → la galería es de solo lectura: se omite
 * el botón "+ Nuevo briefing", que lleva al editor.
 */
export function renderGalleryShell(user: AuthenticatedPilot | null): string {
  const newBtn =
    user !== null
      ? `<a href="/briefing.html" class="bgallery-new-btn">+ Nuevo briefing</a>`
      : '';
  return `
    <div class="bgallery-stage">
      <header class="bgallery-header">
        <h1 class="bgallery-title">BRIEFINGS</h1>
        ${newBtn}
      </header>
      <div class="bgallery-table-wrap">
        <table class="bgallery-table">
          <thead>
            <tr>
              <th class="bgallery-col-pilot">PILOTO</th>
              <th class="bgallery-col-date">FECHA MISIÓN</th>
              <th class="bgallery-col-mission">MISIÓN</th>
              <th class="bgallery-col-campaign">CAMPAÑA</th>
              <th class="bgallery-col-photos">FOTOS</th>
              <th class="bgallery-col-vis">VIS</th>
              <th class="bgallery-col-updated">ACTUALIZADO</th>
              <th class="bgallery-col-actions" aria-label="Acciones"></th>
            </tr>
          </thead>
          <tbody data-bgallery-rows>
            <tr><td colspan="8" class="bgallery-loading">cargando…</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  `;
}

/**
 * Una fila de briefing. Click en cualquier celda → ficha con ?id=.
 *
 * `user === null` (anónimo) → no se renderiza el botón X de borrado.
 */
export function renderRow(
  b: Briefing,
  missionsCache: Map<string, BriefingMission>,
  declaredMissionsCache: Map<string, MissionSummary>,
  user: AuthenticatedPilot | null,
): string {
  const safeId = escapeHtml(b.id);
  const callsign = escapeHtml(b.pilotCallsign);
  const date = b.missionDate ? escapeHtml(b.missionDate) : '—';
  const mission = escapeHtml(
    resolveTargetLabel(b, missionsCache, declaredMissionsCache),
  );
  const campaign = escapeHtml(resolveCampaignLabel(b, declaredMissionsCache));
  const photoCount = String(b.photos.length);
  const visClass =
    b.visibility === 'public' ? 'bgallery-vis--public' : 'bgallery-vis--private';
  const visText = b.visibility === 'public' ? 'Público' : 'Privado';
  const updated = formatRelativeOrIso(b.updatedAt);
  const deleteBtn =
    user !== null && canDeleteBriefing(user, b)
      ? `<button type="button" class="bgallery-delete-btn" data-bgallery-delete="${safeId}" title="Borrar briefing" aria-label="Borrar briefing">✕</button>`
      : '';

  return `
    <tr class="bgallery-row" data-briefing-id="${safeId}" tabindex="0">
      <td class="bgallery-col-pilot">${callsign}</td>
      <td class="bgallery-col-date">${date}</td>
      <td class="bgallery-col-mission">${mission}</td>
      <td class="bgallery-col-campaign">${campaign}</td>
      <td class="bgallery-col-photos bgallery-cell-num">${photoCount}</td>
      <td class="bgallery-col-vis"><span class="bgallery-vis-badge ${visClass}">${visText}</span></td>
      <td class="bgallery-col-updated">${escapeHtml(updated)}</td>
      <td class="bgallery-col-actions">${deleteBtn}</td>
    </tr>
  `;
}

/**
 * Estado vacío. Para usuarios con sesión invita a crear el primero;
 * para anónimos solo informa (sin enlace al editor).
 */
export function renderEmptyRow(user: AuthenticatedPilot | null): string {
  const cta =
    user !== null ? ` <a href="/briefing.html">Crear el primero →</a>` : '';
  return `
    <tr>
      <td colspan="8" class="bgallery-empty">
        Aún no hay briefings.${cta}
      </td>
    </tr>
  `;
}

/** Estado de error genérico. */
export function renderErrorRow(message: string): string {
  return `
    <tr>
      <td colspan="8" class="bgallery-error">${escapeHtml(message)}</td>
    </tr>
  `;
}

/**
 * Resuelve la asignación del briefing a una etiqueta legible. Prioriza
 * la asignación moderna a misión (Fase 8e+); cae al legacy
 * `missionReportId` (Fase 7b) si está fijado; o "—" sin asignar.
 */
function resolveTargetLabel(
  b: Briefing,
  missionsCache: Map<string, BriefingMission>,
  declaredMissionsCache: Map<string, MissionSummary>,
): string {
  if (b.missionId) {
    const m = declaredMissionsCache.get(b.missionId);
    return m ? m.name : `(misión ${b.missionId.slice(0, 8)}…)`;
  }
  if (b.missionReportId) {
    const m = missionsCache.get(b.missionReportId);
    if (m) return m.title?.trim() || m.reportNumber;
    return `(reporte ${b.missionReportId.slice(0, 8)}…)`;
  }
  return '—';
}

/**
 * Resuelve la campaña del briefing derivándola de la misión (no se
 * guarda directamente desde Fase 8f.3). Devuelve "—" si la misión es
 * suelta, no está asignada, o solo hay un missionReportId legacy.
 */
function resolveCampaignLabel(
  b: Briefing,
  declaredMissionsCache: Map<string, MissionSummary>,
): string {
  if (!b.missionId) return '—';
  const m = declaredMissionsCache.get(b.missionId);
  if (!m) return '—';
  return m.campaignName ?? '—';
}

/**
 * Formatea un ISO timestamp a algo legible. Para hoy: "hoy HH:MM";
 * para ayer: "ayer HH:MM"; más viejo: "YYYY-MM-DD".
 */
function formatRelativeOrIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `hoy ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return `ayer ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return d.toISOString().slice(0, 10);
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
